import * as React from "react";
import { useNavigate, Link, useLocation, Outlet } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Popover,
} from "@mui/material";

import {
  ExpandLess,
  ExpandMore,
  Logout,
} from "@mui/icons-material";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import MopedOutlinedIcon from "@mui/icons-material/MopedOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import QuickActionsPopup from "./QuickActionsPopup";
import GlobalSearch from "./GlobalSearch";
import Notifications from "../pages/Notifications/Notifications";

const DRAWER_OPEN = "16rem";
const DRAWER_CLOSED = "4.5rem";

interface MenuItem {
  label: string;
  icon: React.ReactNode | string;
  activeIcon?: string;
  path?: string;
  children?: { label: string; path: string }[];
}

const menuConfig: MenuItem[] = [
  { label: "Dashboard", icon: <DashboardOutlinedIcon />, path: "/dashboard" },
  {
    label: "Inventory",
    icon: <WarehouseOutlinedIcon />,
    children: [
      { label: "Items", path: "/inventory/items" },
      { label: "Transfers", path: "/inventory/transfers" },
    ],
  },
  { label: "Orders", icon: <ShoppingCartOutlinedIcon />, path: "/orders" },
  // { label: "Hubs", icon: <FmdGoodOutlinedIcon />, path: "/hubs" },
  { label: "Delivery", icon: <MopedOutlinedIcon />, path: "/delivery" },
  // {
  //   label: "Outbound",
  //   icon: "/outbound.svg",
  //   activeIcon: "/outboundActive.svg",
  //   path: "/outbound",
  // },
  // { label: "Fleets", icon: <LocalShippingOutlinedIcon />, path: "/fleets" },
  {
    label: "User Management",
    icon: <GroupsOutlinedIcon />,
    children: [
      { label: "Departments", path: "/users/departments" },
      { label: "Roles", path: "/users/roles" },
      { label: "Users", path: "/users/user" },
    ],
  },
  {
    label: "Settings",
    icon: <SettingsOutlinedIcon />,
    children: [
      { label: "Warehouse Settings", path: "/settings/warehouse" },
      { label: "Inventory Settings", path: "/settings/inventory" },
    ],
  },
];

const NavigationBar = () => {
  const [collapsed, setCollapsed] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState<{ [key: string]: boolean }>({});
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLElement | null>(null);
  const [popoverItems, setPopoverItems] = React.useState<{ label: string; path: string }[]>([]);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [quickActionsAnchor, setQuickActionsAnchor] = React.useState<HTMLElement | null>(null);
  const [searchValue, setSearchValue] = React.useState("");
  const [searchFieldRef, setSearchFieldRef] = React.useState<HTMLElement | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    // Auto-expand menus based on current path
    const currentPath = location.pathname;
    const newOpenMenu: { [key: string]: boolean } = {};
    
    menuConfig.forEach((item) => {
      if (item.children) {
        const shouldOpen = item.children.some((child) => currentPath.startsWith(child.path));
        if (shouldOpen) {
          newOpenMenu[item.label] = true;
        }
      }
    });
    
    setOpenMenu(newOpenMenu);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname.startsWith(path);

  const toggleMenu = (label: string) =>
    setOpenMenu((prev) => ({ ...prev, [label]: !prev[label] }));

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const getActiveMenuName = () => {
    if (location.pathname === "/profile") {
      return "My Profile";
    }
    for (let menu of menuConfig) {
      if (menu.path && location.pathname.startsWith(menu.path)) {
        return menu.label;
      }
      if (menu.children) {
        for (let child of menu.children) {
          if (location.pathname.startsWith(child.path)) {
            return child.label;
          }
        }
      }
    }
    return "";
  };

  const activeMenuName = getActiveMenuName();

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <CssBaseline />

      <Drawer
        variant="permanent"
        sx={{
          width: collapsed ? DRAWER_CLOSED : DRAWER_OPEN,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: collapsed ? DRAWER_CLOSED : DRAWER_OPEN,
            display: "flex",
            flexDirection: "column",
            transition: "0.25s",
            overflow: "hidden",
            bgcolor: "background.default",
            borderRight: "1px solid",
            borderColor: "grey.100",
          },
        }}
      >
        <Box
          sx={{
            height: "3.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            px: collapsed ? 0 : "16px",
            borderBottom: "1px solid",
            borderRight: "1px solid",
            borderColor: "grey.100",
            backgroundColor: "background.paper",
            transition: "all 0.3s ease",
          }}
        >
          <Box
            component="img"
            src="/sidebar_Logo.png"
            alt="logo"
            sx={{
              height: collapsed ? 32 : 52,
              width: collapsed ? 32 : "auto",
              transition: "all 0.3s ease",
              objectFit: "contain",
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: "0.5rem",
            py: "0.75rem",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#CBD5E1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#94A3B8",
            },
          }}
        >
          <List>
            {menuConfig.map((item) => {
              const parentActive = item.path
                ? isActive(item.path)
                : item.children?.some((c) => isActive(c.path));

              return (
                <React.Fragment key={item.label}>
                  <ListItemButton
                    component={!item.children ? Link : "div"}
                    to={!item.children ? item.path : undefined}
                    selected={parentActive}
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      if (item.children && collapsed) {
                        setPopoverAnchor(e.currentTarget);
                        setPopoverItems(item.children);
                      } else if (item.children) {
                        toggleMenu(item.label);
                      }
                    }}
                    sx={{
                      height: "2.6rem",
                      borderRadius: "0.5rem",
                      mb: "0.25rem",
                      px: collapsed ? "0.5rem" : "0.75rem",
                      color: parentActive ? "primary.main" : "grey.700",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                      "&.Mui-selected": {
                        border: "1px solid",
                        borderColor: "primary.main",
                        borderLeftWidth: "0.25rem",
                        borderBottomWidth: "0.25rem",
                        backgroundColor: "background.paper",
                        fontWeight: 600,
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "background.paper",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "2.5rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {typeof item.icon === "string" ? (
                        <img
                          src={
                            parentActive && item.activeIcon
                              ? item.activeIcon
                              : item.icon
                          }
                          alt={item.label}
                          style={{
                            width: 20,
                            height: 20,
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            color: parentActive
                              ? "primary.main"
                              : "grey.500",
                          }}
                        >
                          {item.icon}
                        </Box>
                      )}
                    </ListItemIcon>

                    {!collapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: parentActive ? 600 : 400,
                          color: parentActive
                            ? "primary.main"
                            : "grey.700",
                        }}
                      />
                    )}

                    {!collapsed &&
                      item.children &&
                      (openMenu[item.label] ? (
                        <ExpandLess
                          sx={{
                            color: parentActive
                              ? "primary.main"
                              : "grey.500",
                          }}
                        />
                      ) : (
                        <ExpandMore
                          sx={{
                            color: parentActive
                              ? "primary.main"
                              : "grey.500",
                          }}
                        />
                      ))}
                  </ListItemButton>

                  {item.children && !collapsed && (
                    <Collapse in={openMenu[item.label]}>
                      <List>
                        {item.children.map((child) => {
                          const active = isActive(child.path);

                          return (
                            <ListItemButton
                              key={child.label}
                              component={Link}
                              to={child.path}
                              selected={active}
                              disableRipple
                              sx={{
                                pl: "2.5rem",
                                height: "2.4rem",
                                borderRadius: "0.4rem",
                                color: active ? "primary.main" : "grey.100",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: "transparent",
                                },
                                "&.Mui-selected:hover": {
                                  backgroundColor: "transparent",
                                },
                              }}
                            >
                              <ListItemText
                                primary={child.label}
                                primaryTypographyProps={{
                                  fontSize: "0.85rem",
                                  color: active
                                    ? "primary.main"
                                    : "grey.500",
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>

        <Box>
          <ListItemButton 
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              height: "2.6rem",
              borderRadius: "0.5rem",
              mb: "0.25rem",
              px: collapsed ? "0.5rem" : "0.75rem",
              color: "grey.700",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "grey.500",
              }}
            >
              <Box
                component="img"
                src="/collapse.svg"
                alt="Collapse"
                sx={{ width: "1.5rem", height: "1.5rem" }}
              />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText 
                primary="Collapse" 
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: 400,
                  color: "grey.700",
                }}
              />
            )}
          </ListItemButton>

          <Divider sx={{ mb: "0.5rem" }} />

          <ListItemButton
            component={Link}
            to="/organization"
            selected={isActive("/organization")}
            sx={{
              height: "2.6rem",
              borderRadius: "0.5rem",
              mb: "0.25rem",
              px: collapsed ? "0.5rem" : "0.75rem",
              color: isActive("/organization") ? "primary.main" : "grey.700",
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&.Mui-selected": {
                border: "1px solid",
                borderColor: "primary.main",
                borderLeftWidth: "0.25rem",
                borderBottomWidth: "0.25rem",
                backgroundColor: "background.paper",
                fontWeight: 600,
                mx: "0.5rem",
              },
              "&.Mui-selected:hover": {
                backgroundColor: "background.paper",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CorporateFareOutlinedIcon sx={{ color: isActive("/organization") ? "primary.main" : "grey.500" }} />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary="Organization"
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: isActive("/organization") ? 600 : 400,
                  color: isActive("/organization") ? "primary.main" : "grey.700",
                }}
              />
            )}
          </ListItemButton>

          <ListItemButton
            onClick={handleLogout}
            sx={{
              color: "error.main",
              borderRadius: "8px",
              background: "rgba(255, 59, 48, 0.05)",
              border: "1px solid rgba(255, 59, 48, 0.2)",
              mx: "0.5rem",
              mb: "0.5rem",
              height: "40px",
              paddingLeft: "16px",
              alignItems: "center",
              gap: "12px",
              flexShrink: 0,
              alignSelf: "stretch",
            }}
          >
            <ListItemIcon sx={{ color: "error.main", minWidth: "2.5rem" }}>
              <Logout />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Logout" />}
          </ListItemButton>
        </Box>
      </Drawer>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box
          sx={{
            height: "3.5rem",
            px: "1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "grey.100",
            bgcolor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Typography fontWeight={600}>{activeMenuName}</Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search by Container no., package no and Orde..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              inputRef={(ref) => ref && setSearchFieldRef(ref)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#61708A", fontSize: "20px" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: "400px",
                mr: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  bgcolor: "background.default",
                  "& fieldset": { borderColor: "grey.100" },
                  "&:hover fieldset": { borderColor: "grey.100" },
                  "&.Mui-focused fieldset": { borderColor: "grey.100" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  padding: "8px",
                },
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => setQuickActionsAnchor(e.currentTarget)}
              sx={{
                borderRadius: "6px",
                background: "#005EFF",
                boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
                width: 30,
                height: 30,
                mr:3,
                aspectRatio: "1/1",
                "&:hover": { background: "#005EFF" },
              }}
            >
              <AddIcon sx={{ color: "white", fontSize: "18px" }} />
            </IconButton>
            {/* <IconButton size="small" onClick={() => setNotificationsOpen(true)}>
              <NotificationsNoneOutlinedIcon sx={{ color: "#61708A",fontSize:"30px" }} />
            </IconButton> */}
            <IconButton size="small" onClick={() => navigate("/profile")}>
              <AccountCircleOutlinedIcon sx={{ color: "#61708A",fontSize:"30px" }} />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            bgcolor: "background.default",
            overflow: "hidden",
            height: "calc(100vh - 3.5rem)",
          }}
        >
          <Outlet />
        </Box>
      </Box>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
      >
        <List sx={{ minWidth: "10rem" }}>
          {popoverItems.map((child) => (
            <ListItemButton
              key={child.label}
              component={Link}
              to={child.path}
              onClick={() => setPopoverAnchor(null)}
            >
              <ListItemText primary={child.label} />
            </ListItemButton>
          ))}
        </List>
      </Popover>

      <Notifications open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
      <QuickActionsPopup 
        anchorEl={quickActionsAnchor} 
        onClose={() => setQuickActionsAnchor(null)}
        onAddUser={() => {
          navigate("/users/user?action=invite");
        }}
        onAddRole={() => {
          navigate("/users/roles?action=add");
        }}
        onAddDept ={() => {
          navigate("/users/departments?action=add");
        }}
        onAddItem={() => {
          navigate("/inventory/items?action=add");
        }}
        onMoveItem={() => {
          navigate("/inventory/transfers?action=move");
        }}
        onReceiveItem={() => {
          navigate("/inventory/transfers?action=receive");
        }}
        onAddOrder ={()=> {
          navigate("/orders?action=add");
        }}
        onAssignPackages={() => {
          navigate("/delivery?action=assign");
        }}
        onAddPincode={() => {
          navigate("/settings/warehouse?action=pincodes");
        }}
      />
      <GlobalSearch
        anchorEl={searchFieldRef}
        onClose={() => setSearchValue("")}
        searchValue={searchValue}
        onResultClick={(result) => {
          setSearchValue("");
          navigate(`/delivery?packageId=${result.packageBarcode}`);
        }}
      />
    </Box>
  );
};
export default NavigationBar;
